import { useState, useEffect } from "react"
import * as brands from "brands"
import * as api from "../api"
import * as routing from "../routing"
import * as v2layouts from "../layouts"
import * as cache from "../cache"
import * as authzc from "authzcached"
import Container from "../container"
import { SubNavigation } from "../subnav"
import { AssetGrid } from "../review/grid"

export default function AllAssetsContainer(): JSX.Element {
	const cached = cache.useCached()
	const brand = brands.caching.useCached()
	const metaauthz = authzc.useCache((cached) => cached.meta)

	const [assets, setAssets] = useState<api.AssetExtract[]>([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		loadAllAssets()
	}, [cached.documents])

	const loadAllAssets = async (): Promise<void> => {
		setLoading(true)
		try {
			const allAssets: api.AssetExtract[] = []

			// Load assets from all documents
			for (const doc of cached.documents) {
				const req = api.zeros.assets.searchZero({
					document_id: doc.id,
					review_status: "",
					asset_type: "",
				})
				const response = await api.assets.search(brand.id, doc.id, req, authzc.bearer(metaauthz))
				if (response.assets) {
					allAssets.push(...response.assets)
				}
			}

			setAssets(allAssets)
		} catch (err) {
			console.error("unable to load assets", err)
		} finally {
			setLoading(false)
		}
	}

	const handleApprove = async (assetId: string): Promise<void> => {
		const asset = assets.find((a) => a.id === assetId)
		if (!asset || !asset.document_id) return

		try {
			const response = await api.assets.approve(brand.id, assetId, asset.document_id, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to approve asset", err)
		}
	}

	const handleReject = async (assetId: string): Promise<void> => {
		const asset = assets.find((a) => a.id === assetId)
		if (!asset || !asset.document_id) return

		try {
			const response = await api.assets.reject(brand.id, assetId, asset.document_id, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to reject asset", err)
		}
	}

	const handleConfigure = async (assetId: string, config: api.AssetConfigureRequest): Promise<void> => {
		const asset = assets.find((a) => a.id === assetId)
		if (!asset || !asset.document_id) return

		try {
			const response = await api.assets.configure(brand.id, assetId, asset.document_id, config, authzc.bearer(metaauthz))
			// Merge the response with existing asset data to preserve all fields
			setAssets((prev) => prev.map((a) => (a.id === assetId && response.asset ? { ...a, ...response.asset } : a)))
		} catch (err) {
			console.error("unable to configure asset", err)
		}
	}

	if (loading) {
		return (
			<Container title="Review Assets" urlgen={routing.allAssets}>
				<SubNavigation />
				<v2layouts.ContentArea>
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
						Loading assets...
					</div>
				</v2layouts.ContentArea>
			</Container>
		)
	}

	if (assets.length === 0) {
		return (
			<Container title="Review Assets" urlgen={routing.allAssets}>
				<SubNavigation />
				<v2layouts.ContentArea>
					<v2layouts.EmptyState>
						<v2layouts.EmptyStateTitle>No assets found</v2layouts.EmptyStateTitle>
						<v2layouts.EmptyStateText>
							Upload documents to extract and review brand assets
						</v2layouts.EmptyStateText>
					</v2layouts.EmptyState>
				</v2layouts.ContentArea>
			</Container>
		)
	}

	return (
		<Container title="Review Assets" urlgen={routing.allAssets}>
			<SubNavigation />
			<v2layouts.ContentArea>
				<AssetGrid
					assets={assets}
					selectedAssets={cached.selectedAssets}
					onSelectAsset={(id) => {
						if (cached.selectedAssets.has(id)) {
							cached.selectedAssets.delete(id)
						} else {
							cached.selectedAssets.add(id)
						}
						cached.setSelectedAssets(new Set(cached.selectedAssets))
					}}
					onApprove={handleApprove}
					onReject={handleReject}
					onConfigure={handleConfigure}
				/>
			</v2layouts.ContentArea>
		</Container>
	)
}
